import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import CTAButton from '@/components/ui/CTAButton';

import { Sentry } from '@/sentry';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** If true, renders a compact inline fallback (for route-level usage) */
  inline?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Bleibt drin fuer DevTools-Sichtbarkeit.
    console.error('[ErrorBoundary]', error, errorInfo);

    // Nur an Sentry weiterreichen, wenn Sentry initialisiert ist (DSN gesetzt).
    // In lokaler Dev-Umgebung ohne DSN ist getClient() undefined -> still no-op.
    if (!Sentry.getClient()) {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('boundary', 'inner-route');
      Sentry.captureException(error, {
        extra: {
          componentStack: errorInfo.componentStack,
          digest: (errorInfo as ErrorInfo & { digest?: string }).digest,
        },
      });
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.inline) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ flex: 1, p: 6, minHeight: 300 }}
        >
          <Typography variant="seitentitel" component="p" sx={{ color: 'text.secondary', mb: 2 }}>
            Etwas ist schiefgelaufen
          </Typography>
          <Typography variant="meta" sx={{ color: 'text.primary', mb: 4, textAlign: 'center', maxWidth: 400 }}>
            Dieser Bereich konnte nicht geladen werden. Versuchen Sie es erneut
            oder laden Sie die Seite neu.
          </Typography>
          <Stack direction="row" gap={2}>
            <CTAButton variant="outlined" size="small" autoWidth label="Erneut versuchen" onClick={this.handleReset} />
            <CTAButton variant="secondary" size="small" autoWidth label="Seite neu laden" onClick={this.handleReload} />
          </Stack>
        </Stack>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.screen',
        }}
      >
        <Stack
          alignItems="center"
          sx={{
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: '8px',
            p: 6,
            maxWidth: 480,
          }}
        >
          <Typography variant="titelGross" component="p" sx={{ color: 'text.secondary', mb: 2 }}>
            Ein Fehler ist aufgetreten
          </Typography>
          <Typography variant="meta" sx={{ color: 'text.primary', mb: 4, textAlign: 'center' }}>
            Die Anwendung ist auf einen unerwarteten Fehler gestossen.
            Bitte laden Sie die Seite neu.
          </Typography>
          <CTAButton variant="secondary" autoWidth label="Seite neu laden" onClick={this.handleReload} />
        </Stack>
      </Box>
    );
  }
}

export default ErrorBoundary;
